import { useCallback, useRef } from 'react';

// Sound frequencies and durations
const SOUNDS = {
  success: { frequency: 880, duration: 150, type: 'sine' },
  error: { frequency: 220, duration: 300, type: 'square' },
  excess: { frequency: 150, duration: 500, type: 'sawtooth' },
  notFound: { frequency: 330, duration: 200, type: 'triangle' }
};

export const useSound = () => {
  const audioContextRef = useRef(null);

  const getAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  }, []);

  const playSound = useCallback((type) => {
    const soundConfig = SOUNDS[type];
    if (!soundConfig) return;

    try {
      const audioContext = getAudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.type = soundConfig.type;
      oscillator.frequency.setValueAtTime(soundConfig.frequency, audioContext.currentTime);

      // Envelope for smoother sound
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.5, audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + soundConfig.duration / 1000);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + soundConfig.duration / 1000);

      // For excess sound, play a sequence
      if (type === 'excess') {
        setTimeout(() => {
          const osc2 = audioContext.createOscillator();
          const gain2 = audioContext.createGain();
          osc2.connect(gain2);
          gain2.connect(audioContext.destination);
          osc2.type = 'sawtooth';
          osc2.frequency.setValueAtTime(100, audioContext.currentTime);
          gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
          gain2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
          osc2.start(audioContext.currentTime);
          osc2.stop(audioContext.currentTime + 0.3);
        }, 200);
      }
    } catch (error) {
      console.error('Error playing sound:', error);
    }
  }, [getAudioContext]);

  const playSuccess = useCallback(() => playSound('success'), [playSound]);
  const playError = useCallback(() => playSound('error'), [playSound]);
  const playExcess = useCallback(() => playSound('excess'), [playSound]);
  const playNotFound = useCallback(() => playSound('notFound'), [playSound]);

  return {
    playSuccess,
    playError,
    playExcess,
    playNotFound
  };
};
